/**
 * Synth voice recipes: small additive/subtractive patches that approximate
 * marimba, bell, pluck, flute, kalimba and steelpan leads, warm/airy/glass
 * pads and a soft bass — all from sine/triangle oscillators, envelopes and a
 * low-pass filter (never square or sawtooth: they read as harsh to young ears).
 *
 * Pure data + a sanitiser for theme audio params (DOM-free).
 */
import type { ThemeAudio } from '@dropstack/core';
import { SCALE_INTERVALS } from './pitch.ts';
import { clampBpm } from './scheduler.ts';

export type SoftWave = 'sine' | 'triangle';

export interface VoicePartial {
  /** Frequency ratio to the note (1 = fundamental; inharmonic ratios give bell/mallet colour). */
  ratio: number;
  /** Relative level (0–1). */
  gain: number;
  wave?: SoftWave;
  /** Multiplies the voice decay time: < 1 makes upper partials die away first (natural for struck sounds). */
  decayScale?: number;
}

export interface VoiceDef {
  partials: readonly VoicePartial[];
  /** Seconds to peak. */
  attack: number;
  /** Time constant (s) of the fall from peak towards the sustain level. */
  decay: number;
  /** Sustain level 0–1 of peak (0 = percussive: the note rings out regardless of its written length). */
  sustain: number;
  /** Release time constant (s) after note-off. */
  release: number;
  /** Low-pass cutoff (Hz) and resonance. */
  lowpassHz: number;
  lowpassQ: number;
  /** Optional filter sweep: the cutoff starts here and glides to lowpassHz (pluck brightness). */
  filterFromHz?: number;
  filterSweepS?: number;
  /** Gentle pitch vibrato (flute). */
  vibrato?: { hz: number; cents: number; delayS: number };
  /** Detuned twin of every partial (± cents) for chorus warmth. */
  detuneCents?: number;
  /** A short band-passed noise transient (mallet click / breath). */
  click?: { gain: number; hz: number; durS: number };
  /** Output level at velocity 1 (keeps voices balanced against each other). */
  gain: number;
  /** Octave offset from the part's register. */
  octave: number;
}

export type LeadName = ThemeAudio['lead'];
export type PadName = Exclude<ThemeAudio['pad'], 'none'>;

export const LEAD_VOICES: Readonly<Record<LeadName, VoiceDef>> = {
  // Wooden bar: strong fundamental + the tuned 4th and 10th partials, which die fast.
  marimba: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 4, gain: 0.14, decayScale: 0.18 },
      { ratio: 9.9, gain: 0.04, decayScale: 0.08 },
    ],
    attack: 0.004,
    decay: 0.32,
    sustain: 0,
    release: 0.08,
    lowpassHz: 4200,
    lowpassQ: 0.5,
    click: { gain: 0.05, hz: 2400, durS: 0.012 },
    gain: 0.3,
    octave: 0,
  },
  // Glockenspiel-like bell with inharmonic partials; softened with a low-pass.
  bell: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 2.76, gain: 0.22, decayScale: 0.5 },
      { ratio: 5.4, gain: 0.08, decayScale: 0.25 },
      { ratio: 8.93, gain: 0.03, decayScale: 0.12 },
    ],
    attack: 0.003,
    decay: 0.9,
    sustain: 0,
    release: 0.2,
    lowpassHz: 5200,
    lowpassQ: 0.4,
    gain: 0.2,
    octave: 0,
  },
  // Plucked string: triangle body with a bright-to-mellow filter sweep.
  pluck: {
    partials: [
      { ratio: 1, gain: 1, wave: 'triangle' },
      { ratio: 2, gain: 0.25, decayScale: 0.4 },
    ],
    attack: 0.003,
    decay: 0.28,
    sustain: 0,
    release: 0.06,
    lowpassHz: 1100,
    lowpassQ: 0.9,
    filterFromHz: 4200,
    filterSweepS: 0.12,
    gain: 0.3,
    octave: 0,
  },
  // Breathy flute: soft attack, sustain, delayed vibrato and a whisper of breath noise.
  flute: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 2, gain: 0.16 },
      { ratio: 3, gain: 0.05 },
    ],
    attack: 0.06,
    decay: 0.2,
    sustain: 0.75,
    release: 0.12,
    lowpassHz: 3200,
    lowpassQ: 0.5,
    vibrato: { hz: 5, cents: 9, delayS: 0.18 },
    click: { gain: 0.03, hz: 1800, durS: 0.08 },
    gain: 0.22,
    octave: 0,
  },
  // Thumb piano: pure tine with a fleeting inharmonic overtone and a tiny click.
  kalimba: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 3.02, gain: 0.08, decayScale: 0.2 },
      { ratio: 5.95, gain: 0.06, decayScale: 0.08 },
    ],
    attack: 0.002,
    decay: 0.6,
    sustain: 0,
    release: 0.1,
    lowpassHz: 3600,
    lowpassQ: 0.4,
    click: { gain: 0.04, hz: 3200, durS: 0.008 },
    gain: 0.3,
    octave: 0,
  },
  // Steel pan: rich near-harmonic partials with a gentle chorus.
  steelpan: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 2, gain: 0.42, decayScale: 0.6 },
      { ratio: 3, gain: 0.16, decayScale: 0.4 },
      { ratio: 4.02, gain: 0.07, decayScale: 0.25 },
    ],
    attack: 0.008,
    decay: 0.55,
    sustain: 0,
    release: 0.12,
    lowpassHz: 3400,
    lowpassQ: 0.5,
    detuneCents: 4,
    gain: 0.18,
    octave: 0,
  },
};

export const PAD_VOICES: Readonly<Record<PadName, VoiceDef>> = {
  warm: {
    partials: [{ ratio: 1, gain: 1, wave: 'triangle' }],
    attack: 0.9,
    decay: 1.5,
    sustain: 0.85,
    release: 0.9,
    lowpassHz: 850,
    lowpassQ: 0.4,
    detuneCents: 7,
    gain: 0.09,
    octave: 0,
  },
  airy: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 2, gain: 0.3 },
    ],
    attack: 1.1,
    decay: 1.5,
    sustain: 0.8,
    release: 1.1,
    lowpassHz: 2400,
    lowpassQ: 0.3,
    detuneCents: 5,
    gain: 0.07,
    octave: 1,
  },
  glass: {
    partials: [
      { ratio: 1, gain: 1 },
      { ratio: 3, gain: 0.12 },
      { ratio: 4, gain: 0.05 },
    ],
    attack: 0.6,
    decay: 1.2,
    sustain: 0.7,
    release: 0.9,
    lowpassHz: 3000,
    lowpassQ: 0.3,
    gain: 0.07,
    octave: 1,
  },
};

/** Round, quiet bass on each bar's chord root. */
export const BASS_VOICE: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1 },
    { ratio: 2, gain: 0.12, wave: 'triangle' },
  ],
  attack: 0.02,
  decay: 0.5,
  sustain: 0.5,
  release: 0.25,
  lowpassHz: 420,
  lowpassQ: 0.5,
  gain: 0.2,
  octave: 0,
};

/** Soft sustained tone for the tension layer (the "pad" half of it). */
export const TENSION_PAD_VOICE: VoiceDef = {
  partials: [
    { ratio: 1, gain: 1 },
    { ratio: 2, gain: 0.2 },
  ],
  attack: 0.5,
  decay: 1,
  sustain: 0.8,
  release: 0.6,
  lowpassHz: 1600,
  lowpassQ: 0.3,
  gain: 0.12,
  octave: 1,
};

/**
 * Velocities of the accompaniment parts (the lead uses each melody note's
 * own velocity, ≤ 1). Named here so the loudness budget can be unit-tested.
 */
export const PART_VELOCITY = {
  /** Each tone of the pad chord on the downbeat. */
  pad: 0.8,
  /** Downbeat bass under a pad. */
  bass: 0.7,
  /** Downbeat bass when the theme has no pad (shorter, so a touch softer). */
  bassNoPad: 0.65,
  /** Extra beat-3 bass pulse when the theme has no pad. */
  bassPulse: 0.5,
  /** The tension layer's sustained fifth. */
  tensionPad: 0.7,
} as const;

/** The tension layer's soft "egg shaker": a short, high-passed noise grain on every eighth note. */
export const TENSION_SHAKER = {
  durS: 0.05,
  attackS: 0.006,
  /** On-beat grain level; off-beats are accented, like a real shaker. */
  gain: 0.25,
  offbeatGain: 0.45,
  filter: { type: 'highpass', hz: 6500, q: 0.7 },
} as const;

/** Ambience blips (bird chirps, cricket pulses, bubbles): a pure sine with a soft envelope. */
export const CHIRP_VOICE: VoiceDef = {
  partials: [{ ratio: 1, gain: 1 }],
  attack: 0.006,
  decay: 0.05,
  sustain: 0.7,
  release: 0.02,
  lowpassHz: 7000,
  lowpassQ: 0.3,
  gain: 1,
  octave: 0,
};

/** Used when music starts before any theme was set, or theme data is unusable. */
export const DEFAULT_THEME_AUDIO: Readonly<ThemeAudio> = {
  key: 'C',
  scale: 'majorPentatonic',
  bpm: 92,
  lead: 'marimba',
  pad: 'warm',
  ambience: 'birds',
};

const AMBIENCE_KINDS: readonly ThemeAudio['ambience'][] = ['birds', 'waves', 'wind', 'stream', 'insects', 'bubbles', 'none'];

/** True when `value` is an own key of `table` (never an inherited one such as 'toString'). */
function isKeyOf<K extends string>(table: Readonly<Record<K, unknown>>, value: unknown): value is K {
  return typeof value === 'string' && Object.hasOwn(table, value);
}

/** Defensive copy of theme audio params with anything unknown replaced by a safe default. */
export function sanitizeThemeAudio(a: Partial<ThemeAudio> | null | undefined): ThemeAudio {
  const d = DEFAULT_THEME_AUDIO;
  if (!a) return { ...d };
  return {
    key: typeof a.key === 'string' && a.key.trim() ? a.key : d.key,
    scale: isKeyOf(SCALE_INTERVALS, a.scale) ? a.scale : d.scale,
    bpm: clampBpm(typeof a.bpm === 'number' ? a.bpm : d.bpm),
    lead: isKeyOf(LEAD_VOICES, a.lead) ? a.lead : d.lead,
    pad: a.pad === 'none' || isKeyOf(PAD_VOICES, a.pad) ? a.pad : d.pad,
    ambience: a.ambience && AMBIENCE_KINDS.includes(a.ambience) ? a.ambience : d.ambience,
  };
}

/**
 * How long a note's envelope stays open. Percussive voices (sustain 0) ring
 * out naturally for ~3 decay time constants whatever the written length.
 */
export function noteGateS(voice: VoiceDef, durS: number): number {
  return voice.sustain > 0 ? Math.max(durS, voice.attack) : Math.max(durS, voice.attack + voice.decay * 3);
}

/** Seconds from note start until its oscillators can stop (release tail ≈ −50 dB). */
export function noteLifetimeS(voice: VoiceDef, durS: number): number {
  return noteGateS(voice, durS) + voice.release * 6;
}

/** Every voice recipe (for validation tests). */
export function allMusicVoices(): VoiceDef[] {
  return [...Object.values(LEAD_VOICES), ...Object.values(PAD_VOICES), BASS_VOICE, TENSION_PAD_VOICE, CHIRP_VOICE];
}
